import { useState } from "react";
import { Table, Button } from "antd";
import { DownOutlined } from "@ant-design/icons";
import FolderOff from "@/assets/icons/folder_off.svg?react";
import { ScreeningCardConfig, FullRecordConfig } from "./cards.types";

const SCREENING_CARD_NOTE = "Show in order of most recent data";

interface CardProps {
  card: ScreeningCardConfig;
  trialId: string;
  fullRecord: FullRecordConfig;
}

const CardExpandIcon = ({ isActive }: { isActive?: boolean }) => (
  <DownOutlined
    style={{
      transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.3s ease",
      color: "#556C75",
      fontSize: "12px",
    }}
  />
);

const NoFullRecordData = () => (
  <div className="w-full flex items-center flex-col justify-center py-6">
    <FolderOff />
    <p className="text-[#556C75]">No Additional Data</p>
  </div>
);

const getScreenStatusColor = (value: unknown) => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();

  if (
    normalized.includes("pass") ||
    normalized.includes("screened") ||
    normalized === "yes" ||
    normalized === "true" ||
    normalized.includes("complete")
  ) {
    return "#4BA06E";
  }

  if (
    normalized.includes("fail") ||
    normalized === "no" ||
    normalized === "false"
  ) {
    return "#CC3311";
  }

  return "#2B4752";
};

const getBadgePillStyle = (card: ScreeningCardConfig) => ({
  backgroundColor: card.badgeBackgroundColor,
  color: card.badgeTextColor,
  borderRadius: "2px",
  padding: "2px 6px",
  display: "inline-block",
});

const Card = ({ card, trialId, fullRecord }: CardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const recordLabel = card.key === "indication-events" ? "Event" : "Record";
  const showRecordBadge = card.key !== "screening";

  const styledColumns =
    card.key === "indication-events"
      ? fullRecord.columns.map((col) =>
          col.key === "indication_event_name"
            ? {
                ...col,
                render: (text: unknown) => (
                  <span style={getBadgePillStyle(card)}>
                    {(text as React.ReactNode) ?? "-"}
                  </span>
                ),
              }
            : col,
        )
      : fullRecord.columns;

  return (
    <div
      data-testid={`screening-card-${card.key}`}
      style={{ borderRadius: "4px", overflow: "hidden" }}
    >
      <div className="px-6 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex items-center">{card.icon}</div>
            <div className="flex items-baseline gap-2 min-w-0">
              <span
                className="font-semibold"
                style={{ fontSize: "16px", color: "#2B4752" }}
              >
                {card.title}
              </span>
              <span className="text-xs text-[#00000073]">{trialId}</span>
              {showRecordBadge && (
                <span
                  className="font-semibold px-1"
                  style={{
                    fontSize: "14px",
                    color: card.badgeTextColor || "#06709B",
                    borderRadius: "2px",
                    border: "1px solid rgba(0, 0, 0, 0.06)",
                    backgroundColor: card.badgeBackgroundColor || "#ECFAFF",
                  }}
                >
                  {recordLabel}: {fullRecord.dataSource.length}
                </span>
              )}
              <span
                className="italic whitespace-nowrap"
                style={{ color: "rgba(0, 0, 0, 0.45)", fontSize: "12px" }}
              >
                *{SCREENING_CARD_NOTE}
              </span>
            </div>
          </div>
          <Button
            type="text"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-shrink-0"
            aria-expanded={isExpanded}
            aria-label={`Expand ${card.title}`}
            icon={<CardExpandIcon isActive={isExpanded} />}
          />
        </div>
      </div>

      <div className="border-t border-[#0000000f]" />

      <div className="px-6 py-4">
        <div className="overflow-x-auto overflow-y-hidden">
          <div
            className="grid gap-x-6 gap-y-3 min-w-min"
            style={{
              gridTemplateColumns: `repeat(${Object.keys(card.metadata).length}, minmax(150px, auto))`,
            }}
          >
            {Object.entries(card.metadata).map(([key, value]) => (
              <div key={key} className="flex flex-col gap-1">
                <span
                  className="font-medium text-[#556C75]"
                  style={{ fontSize: "14px" }}
                >
                  {key}:
                </span>
                <span
                  className="text-sm"
                  style={
                    key === "Screen Status"
                      ? { color: getScreenStatusColor(value) }
                      : { color: "#2B4752" }
                  }
                >
                  {value ?? "-"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isExpanded && (
        <>
          <div className="border-t border-[#0000000f]" />
          <div className="px-6 py-4 bg-[#F9F9F7]">
            <h3
              className="font-bold mb-[15px]"
              style={{ fontSize: "14px", color: "#000000d9" }}
            >
              FULL RECORD
            </h3>
            <div
              className="border border-[#0000000f] rounded"
              style={
                {
                  "--ant-table-header-bg": "rgba(0, 0, 0, 0.02)",
                } as React.CSSProperties
              }
            >
              <Table
                size="small"
                pagination={false}
                columns={styledColumns}
                dataSource={fullRecord.dataSource}
                rowKey={(_, index) => String(index)}
                locale={{ emptyText: <NoFullRecordData /> }}
              />
            </div>
          </div>
        </>
      )}

      <div className="border-t border-[#0000000f]" />
    </div>
  );
};

export default Card;
